import { navigate } from 'gatsby';
import React, { FC, useEffect, useRef } from 'react';
import '@adyen/adyen-web/dist/adyen.css';
import * as Sentry from '@sentry/browser';

import { ICheckoutForm } from '../../interfaces';
import { I18n, useLanguage } from '../../i18n';
import { useNpDataApi, npBackendApi } from '../../api/npBackend';
import { ErrorBoundary, FallbackProps, useErrorBoundary } from 'react-error-boundary';
import { useNPContext } from '../../npContext';
import { filledButton, fontSize, space, weight } from '../../components/styles';
import constants from '../../constants';
import translate from '../../translations';
import AdyenCheckout from '@adyen/adyen-web';

type PaymentMethodsResponseObject = Record<string, unknown>;
type CoreOptions = Parameters<typeof AdyenCheckout>[0];

type Core = Awaited<ReturnType<typeof AdyenCheckout>>;
type DropIn = ReturnType<Core["createFromAction"]>;

export const CrowdfundingPaymentForm: FC<{
    inputs: ICheckoutForm;
    separateShippingAddress: boolean;
}> = ({ inputs, separateShippingAddress }) => {
    const context = useNPContext();
    const countryCode = context?.state?.centra?.location?.country;
    const language = useLanguage();
    const locale = language === 'nb' ? 'nb-NO' : 'en-US';

    useEffect(() => {
        let selection = context.state?.centra?.selection;

        window.dataLayer &&
            window.dataLayer.push({
                event: 'checkout',
                ecomm_prodid: selection?.items.map(item => item.product.centraProduct),
                ecomm_pagetype: 'cart',
                ecomm_totalvalue: selection?.totals.grandTotalPriceAsNumber,
                ecommerce: {
                    currencyCode: selection?.currency,
                    checkout: {
                        actionField: { step: 2, option: 'payment information' },
                        products: selection?.items.map(item => ({
                            name: item.product.name,
                            id: item.product.centraProduct,
                            price: item.priceEachAsNumber,
                            brand: 'Northern Playground',
                            category: item.product.collectionName,
                            variant: `${translate(
                                language,
                                item.product.gender_selection_gender
                            )} ${item.size} ${translate(language, 'in')} ${
                                item.product.color_swatch.name
                            }`,
                            quantity: item.quantity,
                        })),
                    },
                },
            });
    }, []);

    if (!context) {
        throw new Error('Context is not set');
    }
    if (!context.state.centra) {
        throw new Error('Missing centra-data');
    }
    const selection = context.state?.centra?.selection;
    if (!context) {
        throw new Error('Selection is not set');
    }
    const currency = selection?.currency;
    if (!currency) {
        throw new Error('Currency is not set');
    }
    const [paymentMethods] = useNpDataApi<PaymentMethodsResponseObject>(
        `/api/v1/payment/paymentMethods?selectionToken=${context.state.centra.token}&currency=${currency}&countryCode=${countryCode}&locale=${locale}`
    );

    if (paymentMethods.isLoading) {
        return <div>Loading...</div>;
    }

    if (paymentMethods.error) {
        throw paymentMethods.error;
    }

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <CrowdfundingPaymentFormInner
                paymentMethods={paymentMethods.data}
                locale={locale}
                centraToken={context.state.centra.token}
                inputs={inputs}
                separateShippingAddress={separateShippingAddress}
            />
        </ErrorBoundary>
    );
};

type DropInState = {
    data: unknown;
};

// Payment form for orders with crowdfunding products
const CrowdfundingPaymentFormInner: FC<{
    paymentMethods: PaymentMethodsResponseObject;
    locale: string;
    centraToken: string;
    inputs: ICheckoutForm;
    separateShippingAddress: boolean;
}> = ({ paymentMethods, locale, centraToken, inputs, separateShippingAddress }) => {
    const language = useLanguage();
    const paymentRef = useRef<undefined | { id: number; token: string }>();
    const paymentDivRef = useRef<HTMLDivElement>(null);
    const { showBoundary } = useErrorBoundary();
    const configuration: CoreOptions = {
        paymentMethodsResponse: paymentMethods, // The `/paymentMethods` response from the server.
        clientKey: process.env.GATSBY_ADYEN_CLIENT_KEY, // Web Drop-in versions before 3.10.1 use originKey instead of clientKey.
        locale: locale,
        environment: process.env.GATSBY_ADYEN_ENVIRONMENT,
        onSubmit: (state: DropInState, dropin: DropIn) => {
            dropin.setStatus('loading');
            // Your function calling your server to make the `/payments` request
            npBackendApi
                .post('/api/v1/payment/make_payment', {
                    adyenData: state.data,
                    centraToken: centraToken,
                    language,
                    checkoutForm: {
                        ...inputs,
                        separateShippingAddress,
                    },
                })
                .then(res => {
                    const payment = res.data.payment;
                    paymentRef.current = payment;
                    if (res.data.action) {
                        dropin.setStatus('ready');
                        dropin.handleAction(res.data.action);
                    } else {
                        if (res.data.resultCode === 'Cancelled') {
                            showBoundary(new Error('the payment was cancelled'));
                        }
                        if (res.data.resultCode === 'Error') {
                            showBoundary(new Error('there was an error processing your payment'));
                        } else if (res.data.resultCode === 'Refused') {
                            showBoundary(new Error('the payment was refused'));
                        } else {
                            void navigate(
                                `/${language}/confirmation/crowdfunding?paymentId=${payment.id}&token=${payment.token}`
                            );
                        }
                    }
                })
                .catch(showBoundary);
        },

        onAdditionalDetails: (state: DropInState, dropin: DropIn) => {
            if (!paymentRef.current) {
                showBoundary(
                    new Error('Additional details called before we have got a payment object')
                );
                return;
            }

            const payment = paymentRef.current;
            dropin.setStatus('loading');

            npBackendApi
                .post('/api/v1/payment/additional_details', {
                    adyenData: state.data,
                    paymentId: payment.id,
                    paymentToken: payment.token,
                })
                .then(res => {
                    if (res.data.action) {
                        dropin.setStatus('ready');
                        dropin.handleAction(res.data.action);
                    } else {
                        if (res.data.resultCode === 'Cancelled') {
                            showBoundary(new Error('the payment was cancelled'));
                        }
                        if (res.data.resultCode === 'Error') {
                            showBoundary(new Error('there was an error processing your payment'));
                        } else if (res.data.resultCode === 'Refused') {
                            showBoundary(new Error('the payment was refused'));
                        } else {
                            void navigate(
                                `/${language}/confirmation/crowdfunding/?paymentId=${payment.id}&token=${payment.token}`
                            );
                        }
                    }
                })
                .catch(showBoundary);
        },

        paymentMethodsConfiguration: {
            card: {
                // Example optional configuration for Cards
                // hasHolderName: true,
                // billingAddressRequired: true,
                // holderNameRequired: true,
                // enableStoreDetails: true,
                // hideCVC: false, // Change this to true to hide the CVC field for stored cards
                // name: 'Credit or debit card',
            },
        },
    };

    useEffect(() => {
        if (paymentDivRef.current) {
            const paymentRef = paymentDivRef.current;
            import('@adyen/adyen-web')
                .then(({ default: AdyenCheckout }) => {
                    const checkout = AdyenCheckout(configuration);
                    checkout
                        .then(c => c.create('dropin').mount(paymentRef))
                        .catch(err => {
                            Sentry.captureException(err);
                            console.error(err);
                        });
                })
                .catch(err => {
                    Sentry.captureException(err);
                    console.error(err);
                });
        }
    }, []);

    return <div ref={paymentDivRef}></div>;
};

const ErrorFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    return (
        <div role="alert">
            <h1
                css={`
                    ${fontSize.large};
                    ${weight.regular};
                    margin-bottom: ${space(3)};
                    color: ${constants.colors.pallet.error};
                `}
            >
                <I18n>paymentError</I18n>
            </h1>
            <div
                css={`
                    color: #737373;
                    margin-bottom: ${space(3)};
                `}
            >
                {error && <I18n>{error.message}</I18n>}
            </div>
            <button
                css={`
                    ${filledButton}
                `}
                onClick={resetErrorBoundary}
            >
                <I18n>tryAgain</I18n>
            </button>
        </div>
    );
};
